import { ChiroUpJSON } from '@chiroup/core/functions/ChiroUpJSON';
import {
  del as amplifyDel,
  get as amplifyGet,
  patch as amplifyPatch,
  post as amplifyPost,
  put as amplifyPut,
} from 'aws-amplify/api';
import { checkSessionInvalid } from './apiInterceptor';
import { Safari16API } from './Safari16API';

// Re-export the interceptor's public surface so existing app imports
// (`@chiroup/client-core/functions/ChiroUpAPI`) keep working unchanged.
export { resetUnauthorized, setOnUnauthorized } from './apiInterceptor';
export type { UnauthorizedInfo } from './apiInterceptor';

export const SUPPORT_ACCESS_SESSION_STORAGE_KEY = 'chiroup.supportAccessSessionId';
export const SUPPORT_ACCESS_SESSION_HEADER = 'x-support-access-session-id';
const SUPPORT_ACCESS_EXPIRED_MESSAGE = 'Support access session is not active.';

const withSupportAccessHeaders = (options: any = {}) => {
  if (typeof sessionStorage === 'undefined') return options;

  const sessionId = sessionStorage.getItem(SUPPORT_ACCESS_SESSION_STORAGE_KEY);
  if (!sessionId) return options;

  return {
    ...options,
    headers: {
      ...(options.headers || {}),
      [SUPPORT_ACCESS_SESSION_HEADER]: sessionId,
    },
  };
};

// When the backend reports the support access session is no longer active
// (e.g. left overnight and expired), every subsequent request keeps sending
// the dead session header and 401s — including /me, which leaves the app
// stuck on the loading spinner. Drop the session ID and reload so the user
// continues as themselves without the impersonation. Mirrors the manual
// "End support session" flow in SidenavMenu.
let supportAccessExpiredHandled = false;

const handleSupportAccessExpiredError = (data: any): void => {
  if (supportAccessExpiredHandled) return;
  if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') return;
  if (data?.statusCode !== 401 || data?.message !== SUPPORT_ACCESS_EXPIRED_MESSAGE) return;
  if (!sessionStorage.getItem(SUPPORT_ACCESS_SESSION_STORAGE_KEY)) return;

  supportAccessExpiredHandled = true;
  sessionStorage.removeItem(SUPPORT_ACCESS_SESSION_STORAGE_KEY);
  window.location.assign('/');
};

const buildApiError = (err: any) => {
  let body: Record<string, any> = {};

  if (err?._response?.body) {
    body = ChiroUpJSON.parse(err._response.body, { message: err._response.body });
  }

  if (!err?._response?.body && err?.message) {
    body = { message: err.message };
  }

  if (typeof body === 'string') body = { message: body };

  const statusCode = err?._response?.statusCode || err?.$metadata?.httpStatusCode;
  const data = { statusCode, ...body };

  // Support-access expiry takes precedence — it triggers a hard navigation
  // so the user continues as themselves rather than being signed out. The
  // session-invalid check is still safe to run; if the page is navigating,
  // its side effects don't matter.
  handleSupportAccessExpiredError(data);
  checkSessionInvalid(body, statusCode);

  return {
    response: {
      data,
    },
  };
};
// Detect iOS 16 or lower, or Safari 16 or lower (Amplify API bugs)
// On iOS, all browsers (Chrome, Firefox, etc.) use WebKit engine, so they all have the same bug
const isSafari16OrLower = (() => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';

  // Check for iOS version first (affects all browsers on iOS)
  const iOSMatch = ua.match(/OS ([\d_]+) like Mac OS X/);

  if (iOSMatch) {
    const iOSVersion = iOSMatch[1]?.replace(/_/g, '.') || '';
    const versionParts = iOSVersion.split('.') || [];
    const majorVersion = parseInt(versionParts[0], 10);

    // iOS 16.x or lower - all browsers affected
    if (majorVersion <= 16) {
      return true;
    }
  }

  // Check for desktop Safari version
  const safariMatch = ua.match(/Version\/([\d.]+)/) || [];

  if (safariMatch) {
    const versionParts = safariMatch[1]?.split('.') || [];
    const majorVersion = parseInt(versionParts[0], 10);

    // Safari 16.x or lower
    if (majorVersion <= 16) {
      return true;
    }
  }

  return false;
})();

const get = async <T = any>(apiName: any, path: any, options: any): Promise<T> => {
  options = withSupportAccessHeaders(options);
  // Use Safari16API fallback for Safari 16.0 or lower
  if (isSafari16OrLower) {
    try {
      return await Safari16API.get(apiName, path, options);
    } catch (err: any) {
      handleSupportAccessExpiredError(err?.response?.data);
      throw err;
    }
  }

  if (options.queryParams) {
    // Get rid of any undefined values
    Object.entries(options.queryParams).forEach(([key, value]) => {
      if (value === undefined) {
        delete options.queryParams[key];
      }
    });
  }

  const restOperation = amplifyGet({
    apiName,
    path,
    options,
  });

  // Create a new promise that wraps the response and adds cancel functionality
  const cancellablePromise = new Promise<T>((resolve, reject) => {
    restOperation.response
      .then(response => {
        // If the caller wants a blob, return the raw response without parsing.
        if (options.responseType === 'blob') {
          resolve(response as unknown as T);

          return;
        }

        let returnResponse = null;

        if (response.body) {
          response.body
            .json()
            .then(json => {
              returnResponse = json || null;
              resolve(returnResponse as T);
            })
            .catch(() => {
              resolve(null as T);
            });
        } else {
          resolve(returnResponse as T);
        }
      })
      .catch(err => {
        reject(buildApiError(err));
      });
  }) as Promise<T> & { cancel?: () => void };

  // Add cancel method to the promise
  cancellablePromise.cancel = () => {
    if (restOperation.cancel) {
      restOperation.cancel();
    }
  };

  return cancellablePromise;
};

const put = async <T = any>(apiName: any, path: any, options: any): Promise<T> => {
  options = withSupportAccessHeaders(options);
  // Use Safari16API fallback for Safari 16.0 or lower
  if (isSafari16OrLower) {
    try {
      return await Safari16API.put(apiName, path, options);
    } catch (err: any) {
      handleSupportAccessExpiredError(err?.response?.data);
      throw err;
    }
  }

  const restOperation = amplifyPut({
    apiName,
    path,
    options,
  });

  // Create a new promise that wraps the response and adds cancel functionality
  const cancellablePromise = new Promise<T>((resolve, reject) => {
    restOperation.response
      .then(response => {
        let returnResponse = null;

        if (response.body) {
          response.body
            .json()
            .then(json => {
              returnResponse = json || null;
              resolve(returnResponse as T);
            })
            .catch(() => {
              resolve(null as T);
            });
        } else {
          resolve(returnResponse as T);
        }
      })
      .catch(err => {
        reject(buildApiError(err));
      });
  }) as Promise<T> & { cancel?: () => void };

  // Add cancel method to the promise
  cancellablePromise.cancel = () => {
    if (restOperation.cancel) {
      restOperation.cancel();
    }
  };

  return cancellablePromise;
};

const patch = async <T = any>(apiName: any, path: any, options: any): Promise<T> => {
  options = withSupportAccessHeaders(options);
  // Use Safari16API fallback for Safari 16.0 or lower
  if (isSafari16OrLower) {
    try {
      return await Safari16API.patch(apiName, path, options);
    } catch (err: any) {
      handleSupportAccessExpiredError(err?.response?.data);
      throw err;
    }
  }

  const restOperation = amplifyPatch({
    apiName,
    path,
    options,
  });
  const response = (await restOperation.response.catch(err => {
    throw buildApiError(err);
  })) as any;
  let returnResponse = null;

  if (response.body) {
    const json = await response.body.json().catch(() => {});
    returnResponse = json || null;
  }

  return returnResponse as T;
};

const post = <T = any>(
  apiName: any,
  path: any,
  options: any,
  requestOptions: {
    type?: 'json' | 'blob' | 'text';
  } = {}
): Promise<T> => {
  options = withSupportAccessHeaders(options);
  // Use Safari16API fallback for Safari 16.0 or lower
  if (isSafari16OrLower) {
    return Safari16API.post(apiName, path, options, requestOptions).catch((err: any) => {
      handleSupportAccessExpiredError(err?.response?.data);
      throw err;
    });
  }

  const restOperation = amplifyPost({
    apiName,
    path,
    options,
  });

  // Create a new promise that wraps the response and adds cancel functionality
  const cancellablePromise = new Promise<T>((resolve, reject) => {
    restOperation.response
      .then(response => {
        const returnResponse = null;

        if (response.body) {
          if (requestOptions?.type === 'blob') {
            response.body
              .blob()
              .then(res => {
                resolve(res as T);
              })
              .catch(() => {
                resolve(null as T);
              });
          } else if (requestOptions?.type === 'text') {
            response.body
              .text()
              .then(res => {
                resolve(res as T);
              })
              .catch(() => {
                resolve(null as T);
              });
          } else {
            response.body
              .json()
              .then(res => {
                resolve(res as T);
              })
              .catch(() => {
                resolve(null as T);
              });
          }
        } else {
          resolve(returnResponse as T);
        }
      })
      .catch(err => {
        reject(buildApiError(err));
      });
  }) as Promise<T> & { cancel?: () => void };

  // Add cancel method to the promise
  cancellablePromise.cancel = () => {
    if (restOperation.cancel) {
      restOperation.cancel();
    }
  };

  return cancellablePromise;
};

const del = async <T = any>(apiName: any, path: any, options: any): Promise<T> => {
  options = withSupportAccessHeaders(options);
  // Use Safari16API fallback for Safari 16.0 or lower
  if (isSafari16OrLower) {
    try {
      return await Safari16API.del(apiName, path, options);
    } catch (err: any) {
      handleSupportAccessExpiredError(err?.response?.data);
      throw err;
    }
  }

  if (options.body) {
    console.error(
      'DELETE requests cannot have a body via the amplify library in V6...smh...so this body is not going to be sent.',
      options.body
    );
  }

  const restOperation = amplifyDel({
    apiName,
    path,
    options,
  });
  const response = (await restOperation.response.catch(err => {
    throw buildApiError(err);
  })) as any;
  let returnResponse: T | null = null;

  if (response.body) {
    const json = await response.body.json().catch(() => null);
    returnResponse = (json ?? null) as T;
  }

  return returnResponse as T;
};

export const ChiroUpAPI = {
  get,
  put,
  patch,
  post,
  del,
};
